import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Container, Paper, Typography, CircularProgress, Alert, Button } from '@mui/material'
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material'
import { tenantApi } from '../services/api'
import { loginSuccess } from '../store/authSlice'
import { AppDispatch } from '../store'

const VerifyEmailPage = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'pending_approval' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')

    if (!token) {
      setStatus('error')
      setMessage('Invalid verification link. No token provided.')
      return
    }

    const verify = async () => {
      try {
        const res = await tenantApi.verifyEmail(token)
        const data = res.data

        if (data.status === 'pending_approval') {
          setStatus('pending_approval')
          setMessage(`Your email is verified. Your organization (${data.tenant_name}) requires admin approval. You'll be notified when approved.`)
          return
        }

        if (data.access_token) {
          // Registration complete — log the user in
          localStorage.setItem('token', data.access_token)
          localStorage.setItem('refreshToken', data.refresh_token)
          localStorage.setItem('user', JSON.stringify(data.user))
          dispatch(loginSuccess({ token: data.access_token, user: data.user }))
          setStatus('success')
          setMessage(data.joined_existing
            ? `Welcome! You've joined ${data.tenant?.name}.`
            : 'Your account has been created. Redirecting...'
          )
          setTimeout(() => { window.location.href = '/dashboard' }, 2000)
        }
      } catch (err: any) {
        setStatus('error')
        const detail = err.response?.data?.detail
        setMessage(typeof detail === 'string' ? detail : 'Verification failed. Please try again or sign up again.')
      }
    }

    verify()
  }, [dispatch])

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Box component="img" src="/brand/logo-horizontal.svg" alt="ARIAS" sx={{ height: 36, width: 'auto', display: 'block', mx: 'auto', mb: 3 }} />

          {status === 'loading' && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Verifying your email...</Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Email Verified</Typography>
              <Alert severity="success">{message}</Alert>
            </>
          )}

          {status === 'pending_approval' && (
            <>
              <CheckCircle color="info" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Email Verified</Typography>
              <Alert severity="info">{message}</Alert>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Verification Failed</Typography>
              <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>
              <Button variant="outlined" onClick={() => { window.location.href = '/' }}>
                Back to Sign Up
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  )
}

export default VerifyEmailPage
